import { formatInTimeZone } from 'date-fns-tz';

/**
 * 業務日の基準。ブラウザのタイムゾーンで日付を出すと、日境界の勤務が別の日に見える
 * （Map/displayDays.ts と同じ理由）。
 */
const BUSINESS_TIME_ZONE = 'Asia/Tokyo';

const DAY_MS = 24 * 60 * 60 * 1000;

export type ShiftDateSource = {
  /** 'HH:mm' */
  start: string;
  /** 'HH:mm' */
  end: string;
  /** 勤務開始日時 (ISO)。無ければ日付を出さない */
  startAt?: string | null;
  /** 勤務終了日時 (ISO)。無ければ日付を出さない */
  endAt?: string | null;
};

export type ShiftDateLabel = {
  /** カード本文。'9/21 18:00–翌03:00' */
  label: string;
  /** ツールチップ用の範囲。'9/21 18:00 - 9/22 03:00' */
  range: string;
};

type BusinessDay = { key: string; month: number; day: number; utcMs: number };

function toBusinessDay(iso: string | null | undefined): BusinessDay | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // date-fns の format は timeZone を無視するので formatInTimeZone を使う
  const key = formatInTimeZone(date, BUSINESS_TIME_ZONE, 'yyyy-MM-dd');
  const [y, m, d] = key.split('-').map(Number);
  return { key, month: m, day: d, utcMs: Date.UTC(y, m - 1, d) };
}

const monthDay = (d: BusinessDay) => `${d.month}/${d.day}`;

/**
 * 勤務時間の表示。夜間便が連続すると前日開始と当日開始の勤務が同じ時刻で並ぶので、
 * どの日の勤務か分かるように日付を併記する（表記は route-planner の VehicleInfo に合わせる）。
 *
 * - 日をまたぐ、または表示日 (baseDay) と違う日に始まる勤務は開始側に 'M/D ' を付ける
 * - 翌日に終わるなら '翌HH:mm'、翌々日以降なら 'M/D HH:mm'（「翌々」は分かりづらい）
 */
export function formatShiftDateLabel(shift: ShiftDateSource, baseDay: string | null | undefined): ShiftDateLabel {
  const startDay = toBusinessDay(shift.startAt);
  const endDay = toBusinessDay(shift.endAt);
  if (!startDay || !endDay) {
    return { label: `${shift.start}–${shift.end}`, range: `${shift.start} - ${shift.end}` };
  }

  const dayOffset = Math.round((endDay.utcMs - startDay.utcMs) / DAY_MS);
  const overnight = dayOffset >= 1;
  const showStartDate = overnight || (!!baseDay && startDay.key !== baseDay);
  const startLabel = showStartDate ? `${monthDay(startDay)} ${shift.start}` : shift.start;
  const endLabel = dayOffset >= 2
    ? `${monthDay(endDay)} ${shift.end}`
    : `${overnight ? '翌' : ''}${shift.end}`;

  return {
    label: `${startLabel}–${endLabel}`,
    range: `${monthDay(startDay)} ${shift.start} - ${monthDay(endDay)} ${shift.end}`,
  };
}
